import {
  Button,
  Form,
  Typography,
  Row,
  Col,
  List,
  Collapse,
  InputNumber,
  Alert,
  ConfigProvider,
} from "antd";
import type { CollapseProps } from "antd";
import Icon from "@ant-design/icons";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import BandListEditor, {
  BandListEditorHandle,
  BandRow,
  toBandRows,
} from "../../controls/BandListEditor";
import _ from "lodash";
import { retry } from "../../utils/http";
import { useEffect, useRef, useState } from "react";
import { nameOf } from "../../utils/typescript";
import InModal from "../../controls/InModal";
import { Kpi } from "../../repository/data.default";
import { KpisForm, MatriceForm } from "./KpisFormDialog.default";
import DynamicForm from "../../controls/DynamicForm";
import { setFormFields } from "../../utils/setFormFields";
import { hasFormTouched } from "../../utils/hasFormChanged";
import { QueryOperation } from "../../code/search-query/search-query";
import { InputObject } from "../../controls/default";
import {
  KpiType,
  KpiScoringMethodType,
  Question,
  KpiQuestion,
  KpiMatrix,
  Range,
} from "../../repository/data.default";
import {
  addKpi,
  deleteKpi,
  updateKpi,
  addKpiQuestion,
  deleteKpiQuestions,
  addKpiMatrix,
  getQuestions,
  addKpiMatrixRange,
  updateKpiMatrixRange,
  getKpisQuestions,
  getKpiMatrices,
  updateKpiMatrix,
  getKpiMatrixRanges,
  deleteKpiMatrixRange,
} from "../../repository/data.repository";
import { Icons } from "../../assets/icons";

export interface KpiFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Kpi;
  // The KPI belongs to a published (or retired) questionnaire version, so it is frozen: shown, never saved.
  readonly readOnly?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function KpiFormDialog(props: KpiFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<KpisForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  // Gates the form on open while questions + matrix + ranges load (separate from the submit `loading`).
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(false);
  // A matrix (scale + score-range bands) applies to Matrix-type KPIs only — no separate toggle.
  const isMatrixSelected =
    ((values?.type as InputObject | undefined)?.value ??
      (values?.type as unknown)) === KpiType.Matrix;
  //Questions Form
  const [questionsForm] = Form.useForm();
  const questionsValues = Form.useWatch<{
    readonly question: InputObject;
  }>([], questionsForm);
  const [questions, setQuestions] = useState<InputObject[]>([]);
  //Matrix Form
  const [matrixForm] = Form.useForm();
  const matrixValues = Form.useWatch<MatriceForm>([], matrixForm);
  const [matrixIntialValues, setMatrixInitialValues] = useState<KpiMatrix>();
  //Ranges (bands) — managed by the shared BandListEditor, flushed via its ref on save.
  const [rangeRows, setRangeRows] = useState<BandRow[]>([]);
  const rangesEditorRef = useRef<BandListEditorHandle>(null);

  //Lifecycle

  useEffect(() => {
    if (props.open && props.initialValues && props.mode === "update") {
      setFormFields(form, props.initialValues);

      const questionsQuery = {
        searchQuery: {
          conditions: [
            {
              field: nameOf<KpiQuestion>("kpiDefinitionUid"),
              operation: QueryOperation.Equal,
              values: [props.initialValues.uid],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      };

      const matrixQuery = {
        searchQuery: {
          conditions: [
            {
              field: nameOf<KpiMatrix>("kpiDefinitionUid"),
              operation: QueryOperation.Equal,
              values: [props.initialValues.uid],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      };
      setDataLoading(true);
      Promise.all([
        getKpisQuestions(questionsQuery),
        getKpiMatrices(matrixQuery),
      ])
        .then(([questionsData, matrixData]) => {
          // Questions
          const questions = questionsData.data.map((item) => ({
            display: item.questionDescription,
            value: item.uid,
            dataItem: { ...item, __type: "update" },
          }));
          setQuestions(questions);

          // Matrix — only Matrix-type KPIs have one; Basic KPIs (e.g. Problem Recognition) have none, so skip.
          const matrix = matrixData.data[0];
          if (!matrix) {
            setMatrixInitialValues(undefined);
            setRangeRows([]);
            return;
          }
          setMatrixInitialValues(matrix);
          matrixForm.setFieldsValue({
            expectedValue: matrix.expectedValue,
            standardDeviation: matrix.standardDeviation,
            minValue: matrix.minValue,
            maxValue: matrix.maxValue,
          });

          //Ranges
          return getKpiMatrixRanges({
            searchQuery: {
              conditions: [
                {
                  field: nameOf<Range>("matrixUid"),
                  operation: QueryOperation.Equal,
                  values: [matrix.uid],
                },
              ],
            },
            pageNumber: 0,
            pageSize: 100,
          })
            .then(({ data }) => {
              setRangeRows(toBandRows(data, (r) => r.threshold));
            })
            .catch(() => {
              setError(true);
            });
        })
        .catch(() => {
          setError(true);
        })
        .finally(() => {
          setDataLoading(false);
        });
    } else {
      reset();
      setQuestions([]);
      setRangeRows([]);
    }
  }, [props.open]);

  useEffect(() => {
    if (!isMatrixSelected) {
      form
        .validateFields({
          validateOnly: true,
        })
        .then((value) => {
          setValid(!!Object.values(value).length);
        })
        .catch(() => {
          setValid(false);
        });
    } else {
      matrixForm
        .validateFields({
          validateOnly: true,
        })
        .then((value) => {
          setValid(!!Object.values(value).length);
        })
        .catch(() => {
          setValid(false);
        });
    }
  }, [form, matrixForm, values, props.open, isMatrixSelected, matrixValues]);

  // Events

  function onSubmit(): void {
    const { name, description, shortDescription, type, scoringMethod } = values;

    const { expectedValue, standardDeviation, minValue, maxValue } =
      matrixValues;

    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      // Await every child write (questions, matrix, ranges) before finishing so a failure surfaces and the grid
      // refetches only once everything has actually persisted.
      addKpi({
        name,
        description,
        shortDescription,
        type: type.value,
        scoringMethod: scoringMethod.value,
      })
        .then(async ({ uid }) => {
          const ops: Promise<unknown>[] = [addOrDeleteKpiQuestions(uid)];
          if (isMatrixSelected) {
            const { uid: matrixUid } = await addKpiMatrix({
              kpiDefinitionUid: uid,
              expectedValue: expectedValue ?? null,
              standardDeviation: standardDeviation ?? null,
              minValue,
              maxValue,
            });
            const ranges = rangesEditorRef.current?.persist(matrixUid);
            if (ranges) ops.push(ranges);
          }
          await Promise.all(ops);
        })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "update") {
      const ops: Promise<unknown>[] = [
        addOrDeleteKpiQuestions(props.initialValues?.uid!),
      ];
      if (matrixIntialValues?.uid) {
        const ranges = rangesEditorRef.current?.persist(matrixIntialValues.uid);
        if (ranges) ops.push(ranges);
      }
      if (hasFormTouched(matrixForm)) {
        ops.push(
          updateKpiMatrix({
            uid: matrixIntialValues?.uid,
            rowVersion: matrixIntialValues?.rowVersion,
            expectedValue: expectedValue ?? null,
            standardDeviation: standardDeviation ?? null,
            minValue,
            maxValue,
          }),
        );
      }
      if (hasFormTouched(form)) {
        ops.push(
          updateKpi({
            uid: props.initialValues?.uid,
            rowVersion: props.initialValues?.rowVersion,
            name,
            description,
            shortDescription,
            type: type.value,
            scoringMethod: scoringMethod.value,
          }),
        );
      }
      Promise.all(ops)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteKpi(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function onAddQuestion(): void {
    const { question } = questionsValues || {};
    if (question != null) {
      const updateQuestions = _.uniqBy(
        [
          ...questions.filter(
            (item) => item.dataItem.uid !== question.dataItem.uid,
          ),
          { ...question, dataItem: { ...question.dataItem, __type: "add" } },
        ],
        (item) => item.value,
      );
      setQuestions(updateQuestions);
      questionsForm.resetFields();
    }
  }

  function onRemoveQuestion(uid: string): void {
    const filterQuestions = questions.map((item, i) => {
      if (item.dataItem.uid === uid) {
        return { ...item, dataItem: { ...item.dataItem, __type: "delete" } };
      }

      return item;
    });
    setQuestions(filterQuestions);
  }

  //Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setError(false);
    form.resetFields();
    matrixForm.resetFields();
  }

  const questionnaireQuestionsRequest = (search: string) => {
    return retry(() =>
      getQuestions({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Question>("description"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            // Only a draft version's questions can be scored by a KPI (a published version is frozen), and a KPI
            // scores ONE version: once it has an owner, only that version's questions are offered.
            {
              field: nameOf<Question>("questionnaireStatus"),
              operation: QueryOperation.Equal,
              values: ["Draft"],
            },
            ...(props.initialValues?.questionnaireUid
              ? [
                  {
                    field: nameOf<Question>("questionnaireUid"),
                    operation: QueryOperation.Equal,
                    values: [props.initialValues.questionnaireUid],
                  },
                ]
              : []),
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.questionnaireName
          ? `${dataItem.description} — ${dataItem.questionnaireName} v${dataItem.questionnaireVersion}`
          : dataItem.description,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  function addOrDeleteKpiQuestions(uid: string) {
    const addQuestions = questions.filter(
      ({ dataItem }) => dataItem.__type === "add",
    );
    const deleteQuestions = questions.filter(
      ({ dataItem }) => dataItem.__type === "delete",
    );

    const promises: Promise<void>[] = [];

    if (deleteQuestions.length) {
      deleteQuestions.forEach(({ value }) => {
        promises.push(deleteKpiQuestions(value));
      });
    }

    if (addQuestions.length) {
      addQuestions.forEach(({ value }) => {
        promises.push(
          addKpiQuestion({
            kpiDefinitionUid: uid,
            questionUid: value,
          }),
        );
      });
    }

    return Promise.all(promises);
  }

  // Accordion panels for the right column: Questions (always), and — for Matrix KPIs — a single "Score bands"
  // panel grouping the scale (Min/Max), the optional expected-norm benchmark, and the score-range bands.
  const questionsList = questions.filter(
    ({ dataItem }) => dataItem.__type !== "delete",
  );

  const questionsPanel = {
    key: "questions",
    label: "Questions",
    children: (
      <Col className="flex flex-col gap-2">
        <DynamicForm
          className="w-full"
          form={questionsForm}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    label: "Questions",
                    name: "question",
                    tooltip:
                      "Attach the questionnaire questions whose answers feed this KPI's score.",
                    request: questionnaireQuestionsRequest,
                  },
                ],
              },
            ],
          }}
          onChange={onAddQuestion}
        />
        <Col className="h-[124px] overflow-y-auto rounded-md border-2 border-solid border-gray-300">
          {!!questionsList.length &&
            (props.mode === "add" || props.mode === "update") && (
              <List
                dataSource={questionsList}
                renderItem={(item) => (
                  <List.Item className="p-1 pl-2 pr-2">
                    <Col className="flex flex-auto flex-col">
                      <Typography.Text>{item.display}</Typography.Text>
                    </Col>
                    <Button
                      type="text"
                      danger
                      icon={
                        <Icon
                          className="-ml-0.5 text-xl"
                          component={Icons.CloseIcon}
                        />
                      }
                      onClick={() => onRemoveQuestion(item.dataItem.uid)}
                    />
                  </List.Item>
                )}
              />
            )}
        </Col>
      </Col>
    ),
  };

  const scoreBandsPanel = {
    key: "scoreBands",
    label: "Score bands",
    children: (
      <Col className="flex flex-col gap-3">
        <Form form={matrixForm} autoComplete="off" layout="vertical">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <Typography.Text type="secondary">Scale</Typography.Text>
              <div className="mt-1 flex gap-3">
                <Form.Item
                  className="m-0"
                  label="Min score"
                  tooltip="Lowest possible raw score. Report % = (score − Min) / (Max − Min) × 100."
                  name="minValue"
                  rules={[{ required: true, message: "Min required" }]}
                >
                  <InputNumber />
                </Form.Item>
                <Form.Item
                  className="m-0"
                  label="Max score"
                  tooltip="Highest possible raw score — the top of the scale results are normalized against."
                  name="maxValue"
                  rules={[{ required: true, message: "Max required" }]}
                >
                  <InputNumber />
                </Form.Item>
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">
                Target benchmark (optional)
              </Typography.Text>
              <div className="mt-1 flex gap-3">
                <Form.Item
                  className="m-0"
                  label="Target value"
                  tooltip="The target/expected result for this KPI. Drives the 'vs target' comparison on report evaluation (Below / On / Above target). Optional."
                  name="expectedValue"
                >
                  <InputNumber />
                </Form.Item>
                <Form.Item
                  className="m-0"
                  label="Tolerance (±)"
                  tooltip="Optional ± margin around the target that still counts as 'On target' (e.g. a tolerance of 5 means within ±5 of the target is on target). Leave blank for an exact target."
                  name="standardDeviation"
                >
                  <InputNumber />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
        <div>
          <Typography.Text type="secondary">Bands</Typography.Text>
          <div className="mt-1">
            <BandListEditor
              ref={rangesEditorRef}
              mode={props.readOnly ? "view" : props.mode}
              ownerKeyName="matrixUid"
              thresholdKeyName="threshold"
              initial={rangeRows}
              columns={[
                { key: "label", label: "Label", type: "text" },
                { key: "threshold", label: "Threshold", type: "number" },
                { key: "color", label: "Color", type: "color" },
                {
                  key: "interpretation",
                  label: "Interpretation",
                  type: "textarea",
                },
                {
                  key: "recommendedAction",
                  label: "Recommended action",
                  type: "textarea",
                },
              ]}
              onAdd={addKpiMatrixRange}
              onUpdate={updateKpiMatrixRange}
              onDelete={deleteKpiMatrixRange}
            />
          </div>
        </div>
      </Col>
    ),
  };

  const items: CollapseProps["items"] = isMatrixSelected
    ? [questionsPanel, scoreBandsPanel]
    : [questionsPanel];

  return (
    <InModal
      open={props.open}
      loading={dataLoading}
      loadingLabel="Loading KPI…"
      size={props.mode === "delete" ? "small" : "xxl"}
      title={
        props.readOnly ? `KPI: ${props.initialValues?.name ?? ""}` : modalTitle(props.mode, "KPI", props.initialValues?.name)
      }
      onCancel={onCancel}
      actions={
        props.readOnly ? (
          <Button onClick={onCancel}>Close</Button>
        ) : (
          <ModalFooter
            mode={props.mode}
            loading={loading}
            valid={valid}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        )
      }
    >
      <ConfigProvider componentDisabled={!!props.readOnly}>
      {props.readOnly && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message={`This KPI belongs to ${props.initialValues?.questionnaireName ?? "a questionnaire"} v${props.initialValues?.questionnaireVersion ?? "?"}, which is ${(props.initialValues?.questionnaireStatus ?? "published").toLowerCase()}, so it can no longer be changed. Change it in a new version of the questionnaire.`}
        />
      )}
      {props.mode !== "delete" && (
        <Row className="mb-4 flex flex-row gap-2">
          <Col className="flex basis-1/4 flex-col">
            <DynamicForm
              className="w-auto"
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "textbox",
                        name: "name",
                        label: "Name",
                        rules: [
                          {
                            required: true,
                            message: "Name is required",
                          },
                        ],
                      },
                      {
                        type: "areabox",
                        name: "description",
                        label: "Description",
                        rules: [
                          {
                            required: true,
                            message: "Description is required",
                          },
                        ],
                      },
                      {
                        type: "textbox",
                        name: "shortDescription",
                        label: "Short Description",
                        rules: [
                          {
                            required: true,
                            message: "Short Description is required",
                          },
                        ],
                      },
                      {
                        type: "combobox",
                        name: "type",
                        label: "Type",
                        tooltip:
                          "How the KPI is scored: Basic = a single summed/averaged value with no bands; Matrix = the score is mapped onto a scale + labelled bands (configured under Score bands).",
                        request: () =>
                          Promise.resolve(
                            Object.entries(KpiType).map(([value, display]) => ({
                              value,
                              display,
                            })),
                          ),
                        rules: [
                          {
                            required: true,
                            message: "Type is required",
                          },
                        ],
                      },
                      {
                        type: "combobox",
                        name: "scoringMethod",
                        label: "Scoring Method",
                        tooltip:
                          "How a respondent's answer choices are aggregated into the raw score: Sum adds the choice weights, Average divides the sum by the number of answered questions, Count tallies the selected choices.",
                        request: () =>
                          Promise.resolve(
                            Object.entries(KpiScoringMethodType).map(
                              ([value, display]) => ({
                                value,
                                display,
                              }),
                            ),
                          ),
                        rules: [
                          {
                            required: true,
                            message: "Scoring Method is required",
                          },
                        ],
                      },
                    ],
                  },
                ],
              }}
            />
          </Col>
          <Col className="flex flex-1 flex-col gap-2">
            <Collapse
              items={items}
              defaultActiveKey={["questions", "scoreBands"]}
            />
          </Col>
        </Row>
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="KPI"
          entityName={props.initialValues?.name}
        />
      )}
      {error && <ModalErrorAlert message="Something went wrong, try again" />}
      </ConfigProvider>
    </InModal>
  );
}

export default KpiFormDialog;
